import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom'
import { getCookie } from './storageUtils'
import CustomOverlayTrigger from '../components/CustomOverlayTrigger'
import { gameOverlayHover } from '../constants/overlayTrigger'
import { currencyCodeList } from 'constants/currencyCodeList'
import { BREAK_POINTS_SIZES } from 'constants/uiConfig'
import { getRootDomain, shouldScrollToTopOnRouteChange } from './routes'

export const capitalizeMyText = (name, delemiter) => {
  return (name && name?.length > 0 && name.split(delemiter).map(ele =>
    ele.substr(0, 1).toUpperCase().concat(ele.substr(1))
  ).join(' ')) || name
}

export const countDown = (startMinutes = 1, msg, setValue, windowPopup) => {
  let time = startMinutes * 60 - 1 // Start From 59th second

  const t = setInterval(() => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    const result = `${msg} ${String(minutes)?.length === 1 ? `0${minutes}` : minutes}:${String(seconds)?.length === 1 ? `0${seconds}` : seconds}`
    setValue(result) // Update value using ref
    time--
    if (minutes === 0 && seconds === 0) {
      clearInterval(t)
      windowPopup && windowPopup?.close()
    }
  }, 1000)
}

export const ScrollToTop = () => {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const casinoCategories = useSelector(state => state?.casinoMenu?.gameCategory?.casinoCategories)
  const previousPathnameRef = useRef(pathname)

  // Reset scroll after the new route has committed (layout effect, before paint). Scrolling
  // from click handlers runs while the old page is still rendered, which iOS Safari can ignore
  // and then keep the old offset on the new page. Back/forward (POP) keeps the browser's own restoration.
  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current
    previousPathnameRef.current = pathname

    if (navigationType === NavigationType.Pop) return
    if (shouldScrollToTopOnRouteChange(previousPathname, pathname, casinoCategories)) {
      window.scrollTo(0, 0)
    }
  }, [pathname])

  useEffect(() => {
    // if (process.env.DEBUGGING_ENABLED !== true || process.env.DEBUGGING_ENABLED !== 'true') {
    //   console.log = () => {}
    //   console.warn = () => {}
    //   console.error = () => {}
    // }

    // console.log = () => {}
    // console.warn = () => { }
    // console.error = () => { }
  }, [])

  return null
}

export const isNewGame = (date) => {
  if (date) {
    const currentDate = new Date()
    const gameUpdatedAt = new Date(date)
    const diff = parseInt((currentDate - gameUpdatedAt) / (1000 * 60 * 60 * 24), 10)
    if (diff <= 30 && diff >= 0) {
      return true
    } else {
      return false
    }
  } else {
    return false
  }
}

// Chat widget iframe ids to preserve when clearing frames (payment/game cleanup).
// Intercom: `intercom-frame`
const CHAT_FRAME_IDS = ['intercom-frame', 'launcher']

const isZendeskIFrame = (element) => {
  try {
    if (
      element?.getAttribute('data-product') === 'web_widget' ||
      element?.name === 'Messaging window' ||
      element?.title === 'Number of unread messages'
    ) {
      return true
    }
  } catch (error) {
    console.error('Error checking if element is Zendesk iframe:', error)
  }
}

export const killAllFrames = (payment) => {
  payment && window._PaymentIQCashierReset()
  const iframes = document.querySelectorAll('iframe')
  for (let i = 0; i < iframes.length; i++) {
    if (!CHAT_FRAME_IDS.includes(iframes[i]?.id) && !isZendeskIFrame(iframes[i])) {
      iframes[i].parentNode.removeChild(iframes[i])
    }
  }
}

export const getTimeLeft = (date, formatted) => {
  const newDate = new Date(date).getTime() - new Date().getTime()
  const dd = Math.floor(newDate / (1000 * 60 * 60 * 24))
  const hh = Math.floor((newDate % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const mm = Math.floor((newDate % (1000 * 60 * 60)) / (1000 * 60))
  const ss = Math.floor((newDate % (1000 * 60)) / 1000)
  return formatted
    ? `${dd < 10 ? '0' + dd : dd}d ${hh < 10 ? '0' + hh : hh}h:${mm < 10 ? '0' + mm : mm}m:${ss < 10 ? '0' + ss : ss}s`
    : { dd: dd < 10 ? '0' + dd : dd, hh: hh < 10 ? '0' + hh : hh, mm: mm < 10 ? '0' + mm : mm, ss: ss < 10 ? '0' + ss : ss }
}

export const isMobileOrTablet = () => {
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

  // Adjust these values based on your specific criteria
  const mobileMaxWidth = 767
  const tabletMaxWidth = 991

  if (screenWidth <= mobileMaxWidth) {
    // Mobile
    return true
  } else if (screenWidth <= tabletMaxWidth) {
    // Tablet
    return true
  } else {
    // Laptop or Desktop
    return false
  }
}

/**
 * Checks if the current device is a mobile device or not.
 *
 * This function uses the `navigator.userAgent` to check for specific keywords that match mobile devices
 * (e.g., Android, iPad, iPhone, iPod). It also checks if the viewport width is less than or equal to 1024 pixels,
 * which is typically the threshold for mobile or tablet devices.
 *
 * @returns {boolean} `true` if the device is identified as a mobile device or the viewport width is less than or equal to 1024, `false` otherwise.
 */
export const isMobileDevice = (ignoreDeviceWidth = false) => {
  const userAgent = navigator.userAgent || window.opera
  return ignoreDeviceWidth ? /android|iPad|iPhone|iPod/i.test(userAgent) : /android|iPad|iPhone|iPod/i.test(userAgent) || window.innerWidth <= 1024
}

/**
 * Whether MobileBottomMenu is on screen — it is `d-lg-none`, i.e. rendered only below 992px.
 * Unlike `isMobileDevice()`, this does not include 992–1024px tablets (e.g. iPad Pro 13" portrait).
 */
export const isBottomMenuVisible = () => window.innerWidth <= BREAK_POINTS_SIZES.md

export const useComponentVisible = (initialIsVisible) => {
  const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible)
  const menuRef = useRef(null)
  const toggleRef = useRef(null)

  const showComponent = () => {
    setIsComponentVisible(!isComponentVisible)
  }

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target) && !toggleRef.current.contains(event.target)) {
      setIsComponentVisible(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return { toggleRef, menuRef, isComponentVisible, setIsComponentVisible, showComponent }
}

//  Capitalizes the first letter of a given string.
function capitalizeFirstLetter (string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

// Truncate function for strings with overlay
function truncateTextWithOverlay (text, maxLength = 15) {
  if (text.length > maxLength) {
    return (
      <CustomOverlayTrigger overlayContent={text} {...gameOverlayHover}>
        <div className='loyalty-info-i'>{text.substring(0, maxLength)}...</div>
      </CustomOverlayTrigger>
    )
  }
  return text
}

// Truncates a string if its length exceeds maxLength and appends '...'.
function truncateText (text, maxLength = 12) {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...'
  }
  return text + '...'
}

// Formats inputString as tags, handling JSON-encoded arrays and normal strings.
export function formatTags (inputString) {
  // Check if inputString is a string
  if (typeof inputString !== 'string') {
    // If not a string, return input as is
    return inputString === undefined ? '-' : inputString
  }

  if (inputString === '[]') return '-'

  // Attempt to parse the string as JSON
  try {
    const tagsArray = JSON.parse(inputString)

    // Check if parsed result is an array
    if (Array.isArray(tagsArray)) {
      // Capitalize the first letter of each tag
      const capitalizedTags = tagsArray.map(capitalizeFirstLetter)

      // If array length is more than one, return formatted content
      if (capitalizedTags.length > 1) {
        return (
          <CustomOverlayTrigger overlayContent={capitalizedTags.join(', ')} {...gameOverlayHover}>
            <div className='loyalty-info-i'>{truncateText(capitalizedTags[0])}</div>
          </CustomOverlayTrigger>
        )
      } else if (capitalizedTags.length === 1) {
        // If array length is one, return the single tag
        return truncateTextWithOverlay(capitalizedTags[0])
      }
    }
  } catch (error) {
    // If JSON parsing fails or input is not a valid JSON array string, capitalize the first letter of the input string
    return truncateTextWithOverlay(capitalizeFirstLetter(inputString))
  }

  // If inputString is not a valid JSON array or cannot be parsed, capitalize the first letter of the input string
  return truncateTextWithOverlay(capitalizeFirstLetter(inputString))
}

export function formatPriceWithCommas (price) {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Generates the game URL based on the selected language, game identifier, and device type.
 *
 * @param {string} languageCode - The selected language code for the URL.
 * @param {string | number} id - The game ID.
 * @param {string} gameName - The name of the game.
 * @param {string} gameProvider - The name of the game provider.
 * @param {boolean} [demo=false] - Indicates whether the URL is for a demo game.
 * @returns {string} - The generated game URL according to the device type (mobile or desktop), optionally for a demo.
 */
export const getGameUrl = (
  languageCode,
  id,
  gameName,
  gameProvider,
  demo = false
) => {
  const gameIdentifier = gameName
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s]/g, '')
    ?.trim()
    ?.replace(/\s+/g, '-')

  const providerIdentifier = gameProvider
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s]/g, '')
    ?.trim()
    ?.replace(/\s+/g, '-')

  if (demo && !isMobileDevice()) {
    return `/${languageCode}/demo/${id}/${providerIdentifier}/${gameIdentifier}`
  }

  return isMobileDevice()
    ? `/${languageCode}/play-mobile/${id}/${providerIdentifier}/${gameIdentifier}`
    : `/${languageCode}/play/${id}/${providerIdentifier}/${gameIdentifier}`
}

/**
 * Convert a game identifier (URL-friendly slug) back into a human-readable game name.
 * Mirrors the normalization used in getGameUrl for the gameName part.
 *
 * Example: "god-of-war" -> "God Of War"
 *
 * Note: Since special characters are stripped during slugification, they cannot
 * be restored here (e.g., apostrophes, punctuation).
 *
 * @param {string} gameIdentifier - The URL-friendly game identifier (slug).
 * @returns {string} Human-readable game name.
 */
export const getGameNameFromIdentifier = (gameIdentifier) => {
  if (!gameIdentifier || typeof gameIdentifier !== 'string') return ''
  const cleaned = gameIdentifier
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/gi, '')
  const words = cleaned.split('-').filter(Boolean)
  return words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const isValidUrl = (url) => url && typeof url === 'string' && !url.includes('undefined')

export const getThresholdValue = () => {
  const width = window.innerWidth

  switch (true) {
    case width < 480: return 3
    case width < 600: return 4
    case width < 768: return 5
    case width < 1324: return 6
    case width < 1440: return 8
    default: return 8
  }
}

/**
 * Sorts an array of country objects by name in ascending order.
 * @param {Object} data - The data object containing rows of countries.
 * @param {Array} data.rows - The array of country objects to be sorted.
 * @param {string} data.rows[].name - The name of the country.
 * @returns {Object} The sorted data object with rows sorted by name.
 */

export const sortCountriesByName = (data) => {
  data.rows.sort((a, b) => a.name.localeCompare(b.name))
  return data
}

export const fetchRedirectionUrl = async ({ accessToken, countryCode }) => {
  try {
    const { REACT_APP_API_URL } = process.env
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    // Set countryCode as a header if it exists in the cookie
    if (getCookie('countryCode')) {
      headers['Country-Code-Id'] = getCookie('countryCode') || ''
    }
    // Pass countryCode as a query parameter in the URL
    const response = await fetch(`${REACT_APP_API_URL}/api/tenant/get-redirection-url` + (countryCode ? `?countryCodeId=${countryCode}` : ''), {
      method: 'GET',
      headers
    })

    if (response.ok) {
      const data = await response.json()
      return data?.data?.redirectionUrl || null
    } else {
      throw new Error('Failed to fetch redirection URL')
    }
  } catch (error) {
    console.error('Error fetching redirection URL:', error)
    return null
  }
}

export const cleanHtmlContent = (html) => {
  if (!html) return ''
  // Replace non-breaking spaces with normal spaces
  let cleaned = html.replace(/&nbsp;/g, ' ')
  // Remove display: flex from inline styles
  cleaned = cleaned.replace(/style=["'][^"']*display\s*:\s*flex;?[^"']*["']/gi, '')
  return cleaned
}

/**
 * Checks if HTML content has actual text. whitespaces are not considered as actual text.
 * @param {string} htmlString - The HTML string to check.
 * @returns {boolean} True if the HTML content has actual text, false otherwise.
 */
export const hasActualContent = (htmlString) => {
  if (!htmlString) return false

  // Remove HTML tags
  let textContent = htmlString.replace(/<[^>]*>/g, '')

  // Decode HTML entities by creating a temporary element
  const tempElement = document.createElement('div')
  tempElement.innerHTML = textContent
  textContent = tempElement.textContent || tempElement.innerText || ''

  // Trim whitespace (including regular spaces, tabs, newlines, and non-breaking spaces)
  textContent = textContent.trim()

  return textContent.length > 0
}

/**
 * Formats a string by replacing HTML entities with their corresponding characters.
 * Replaces &nbsp;, &gt;, and &lt; with their respective characters.
 *
 * @param {string} str - The string containing HTML entities to format
 * @returns {string} The formatted string with HTML entities replaced
 *
 * @example
 * // Returns "Hello > World"
 * formattedParsableString("Hello &gt; World")
 *
 * @example
 * // Returns "< Text with spaces >"
 * formattedParsableString("&lt; Text&nbsp;with&nbsp;spaces &gt;")
 */
export const formattedParsableString = (str) => {
  return str?.replaceAll('&nbsp;', ' ').replaceAll('&gt;', '>').replaceAll('&lt;', '<')
}

/**
 * Formats a currency amount with the specified currency code.
 * @param {number} amount - The amount to format.
 * @param {string} currency - The currency code to use.
 * @returns {string} The formatted currency amount.
 */
export const formatCurrency = (amount, currency = 'EUR') => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return '-'
  }
  return `${currencyCodeList[currency] || currency || 'EUR'} ${Number(amount).toFixed(2)}`
}

/**
 * Normalises a host or domain for comparison: drops a leading `www.` and any port.
 * @param {string} [domain] Host such as `www.vibebet.com` or `localhost:3011`.
 * @returns {string} `vibebet.com` or `localhost`; empty string for no input.
 */
export const stripWww = (domain = '') => domain.replace(/^www\./, '').split(':')[0]

/**
 * Whether the app is served from the tenant's main domain (`REACT_APP_WEBSITE_DOMAIN`)
 * rather than a mirror. A leading `www.` and a port are ignored on both sides, so
 * `www.vibebet.com` matches an env value of `vibebet.com` and local ports need not line up.
 * @returns {boolean}
 */
export const isMainDomain = () => {
  const mainDomain = process.env.REACT_APP_WEBSITE_DOMAIN
  if (!mainDomain) return false
  return getRootDomain() === stripWww(mainDomain)
}
